import type { IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AdHocAggregateResult, AdHocTrendResult, AnalyticsFilter, AnalyticsMetric } from '@/shared/analytics/AnalyticsTypes';
import type { TileSpec } from './PinnedTileTypes';

const AGGREGATE_LIMIT = 12;

export interface TileChartData {
    aggregate: AdHocAggregateResult | null;
    trend: AdHocTrendResult | null;
}

/**
 * Runs the analytics query behind a {@link TileSpec}, composing the tile's own
 * filters with the page's live filters — the same composition the assist chart
 * uses (SurfaceAssistViewModel.runChart). Shared by the pinned tiles and the
 * Add-insight modal preview so both draw identically.
 */
export async function runTileChart(gateway: IAnalyticsGateway, spec: TileSpec, pageFilters: AnalyticsFilter[]): Promise<TileChartData> {
    const filters = [...pageFilters, ...(spec.filters ?? [])];
    if (spec.viz === 'trend') {
        const trend = await gateway.trend({ source: 'interactions', bucket: spec.bucket ?? 'week', metrics: [spec.metric], filters });
        return { aggregate: null, trend };
    }
    const aggregate = await gateway.aggregate({
        source: 'interactions',
        groupBy: [spec.primaryField],
        metrics: chartMetrics(spec.metric),
        orderBy: { metric: spec.metric, direction: 'desc' },
        filters,
        limit: spec.limit ?? AGGREGATE_LIMIT,
    });
    return { aggregate, trend: null };
}

/**
 * A composite signature of a spec + filters, for cheap dedupe of repeated query
 * runs. `primaryField` is excluded for `trend` (it does not affect a trend query).
 */
export function tileChartKey(spec: TileSpec, pageFilters: AnalyticsFilter[]): string {
    const filters = [...pageFilters, ...(spec.filters ?? [])];
    const serialized = filters
        .map((filter) => `${filter.field}=${[...filter.values].sort().join('|')}`)
        .sort()
        .join(';');
    const fieldPart = spec.viz === 'trend' ? '' : spec.primaryField;
    const limitPart = spec.viz === 'trend' ? '' : (spec.limit ?? '');
    return `${spec.viz}|${fieldPart}|${spec.metric}|${spec.bucket ?? ''}|${limitPart}|${serialized}`;
}

function chartMetrics(metric: AnalyticsMetric): AnalyticsMetric[] {
    return metric === 'users' ? ['users'] : [metric, 'users'];
}
